import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Save, FileText, Eye } from 'lucide-react';

export default function AdminLegal() {
  const [settings, setSettings] = useState([]);
  const [previewKey, setPreviewKey] = useState(null);

  useEffect(() => {
    api.get('/site-settings/all').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const getValue = (key) => settings.find(s => s.key === key)?.value || '';
  const setValue = (key, value) => setSettings(prev => prev.map(s => s.key === key ? { ...s, value } : s));

  const handleSave = async () => {
    try {
      const keys = ['legal_impressum', 'legal_datenschutz', 'legal_agb', 'cookie_banner_text'];
      const updates = keys.map(k => ({ key: k, value: getValue(k) }));
      await api.put('/site-settings', updates);
      toast.success('Rechtliche Inhalte gespeichert');
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.statusText || e.message || 'Unbekannter Fehler';
      toast.error(`Fehler beim Speichern: ${msg}`);
    }
  };

  // Convert plain text to formatted HTML for preview
  const formatTextForPreview = (text) => {
    if (!text) return '';
    // Split by double newlines for paragraphs, single newlines for line breaks
    return text
      .split(/\n\n+/)
      .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
      .join('');
  };

  const LegalCard = ({ title, keyName, url, placeholder, rows = 10 }) => (
    <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-[#0E518D] flex items-center gap-2">
            <FileText className="h-5 w-5" /> {title}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPreviewKey(previewKey === keyName ? null : keyName)}
            className="text-xs text-slate-500 hover:text-[#0E518D]"
            data-testid={`preview-${keyName}`}
          >
            <Eye className="h-4 w-4 mr-1" /> {previewKey === keyName ? 'Bearbeiten' : 'Vorschau'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Label className="text-xs text-slate-400">Erreichbar unter: {url}</Label>
        
        {previewKey === keyName ? (
          <div className="mt-3 p-4 bg-slate-50 rounded-sm border border-slate-200 min-h-[200px]">
            <p className="text-xs text-slate-400 mb-3">Vorschau der Darstellung:</p>
            <div 
              className="prose prose-slate prose-sm max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: formatTextForPreview(getValue(keyName)) }}
            />
          </div>
        ) : (
          <Textarea 
            value={getValue(keyName)} 
            onChange={e => setValue(keyName, e.target.value)} 
            rows={rows} 
            className="mt-2 rounded-sm text-sm leading-relaxed" 
            placeholder={placeholder}
            data-testid={`legal-${keyName.replace('legal_', '')}-input`}
          />
        )}
        
        <p className="text-xs text-slate-400 mt-2">
          Geben Sie den Text normal ein. Absätze werden durch Leerzeilen getrennt. Zeilenumbrüche werden automatisch übernommen.
        </p>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="admin-legal">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0E518D]">Rechtliches / Legal</h1>
        <Button onClick={handleSave} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid="legal-save">
          <Save className="h-4 w-4 mr-1" /> Speichern
        </Button>
      </div>

      <div className="space-y-6">
        <p className="text-sm text-slate-500">
          Bearbeiten Sie die Inhalte für Impressum, Datenschutz und AGB als normalen Text. 
          Zeilenumbrüche und Absätze werden automatisch formatiert. 
          <span className="text-slate-400 text-xs ml-1">(v2)</span>
        </p>

        <LegalCard 
          title="Impressum" 
          keyName="legal_impressum" 
          url="/impressum"
          placeholder={`GB Sylt GmbH
Friedrichstraße 44
25980 Sylt / Westerland
Deutschland

Telefon: +49 (0)4651 82460
E-Mail: info@gb-sylt.de

Vertreten durch die Geschäftsführung.`}
        />

        <LegalCard 
          title="Datenschutzerklärung" 
          keyName="legal_datenschutz" 
          url="/datenschutz"
          placeholder={`Datenschutzerklärung

Der Schutz Ihrer persönlichen Daten ist uns wichtig. Wir verarbeiten Ihre Daten ausschließlich im Rahmen der gesetzlichen Bestimmungen.

Bei einer Bestellung über diese Webseite werden personenbezogene Daten wie Name, E-Mail-Adresse und Buchungsnummer verarbeitet, um Ihre Bestellung bearbeiten zu können.`}
          rows={12}
        />

        <LegalCard 
          title="AGB" 
          keyName="legal_agb" 
          url="/agb"
          placeholder={`Allgemeine Geschäftsbedingungen

Diese Allgemeinen Geschäftsbedingungen gelten für Zusatzleistungen und Bestellungen über die Bestellplattform der GB Sylt GmbH.

Mit der Bestellung erklärt sich der Kunde mit den Bedingungen einverstanden.

Die Lieferung der bestellten Produkte erfolgt in der Regel zum Anreisetermin direkt in die gebuchte Unterkunft.`}
          rows={12}
        />

        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D] flex items-center gap-2"><FileText className="h-5 w-5" /> Cookie-Banner Text</CardTitle></CardHeader>
          <CardContent>
            <Label className="text-xs text-slate-400">Wird im Cookie-Banner beim ersten Besuch angezeigt.</Label>
            <Textarea 
              value={getValue('cookie_banner_text')} 
              onChange={e => setValue('cookie_banner_text', e.target.value)} 
              rows={3} 
              className="mt-2 rounded-sm text-sm" 
              placeholder="Diese Website verwendet Cookies, um Ihnen ein besseres Erlebnis zu bieten."
              data-testid="legal-cookie-input" 
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
